/**
 * eDMS Web — Entry Point
 *
 * Bootstrap order:
 * 1. Tokens CSS (sets all design variables).
 * 2. Global CSS (page reset + focus-visible).
 * 3. i18n (locale resolved + RTL applied to <html dir>).
 * 4. QueryClient + Router (in that order — router uses QueryClient context).
 * 5. Render <AppRouter />.
 *
 * Owner: Claude 3 (Frontend & UX).
 * URS rows: AUTH-001/008, I18N-001/002.
 */

import React from 'react';
import ReactDOM from 'react-dom/client';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';

import { ToastProvider } from '@edms/design-system';
import '@edms/design-system/tokens.css';
import '@edms/design-system/tokens/tokens-v2.css';
import './styles/global.css';
import './i18n';
import { AppRouter } from './router';
import { loadRuntimeConfig } from './api/runtimeConfig';
import { SiteScopeProvider } from './auth/SiteScopeContext';

// Vite fires `vite:preloadError` when a <link rel=modulepreload> chunk fails to
// load — typically an open tab after a deploy shipped new chunk hashes. Reload
// once (guarded) to fetch the fresh no-cache index.html + new chunk map. Mirrors
// importWithReload() in router.tsx, which covers the dynamic-import path.
window.addEventListener('vite:preloadError', (e) => {
  e.preventDefault();
  if (!sessionStorage.getItem('edms:chunk-reloaded')) {
    sessionStorage.setItem('edms:chunk-reloaded', '1');
    window.location.reload();
  }
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,         // 30s default freshness
      gcTime: 5 * 60_000,        // 5 min cache
      retry: 2,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,                  // Never auto-retry mutations (signatures must be deliberate)
    },
  },
});

// Load /config.json BEFORE we mount React so apiFetch reads the correct
// api_base on its first call. This is the cheapest possible round-trip
// (a few hundred bytes, no-cache via .htaccess) and unblocks tunnel-URL
// rotation without a rebuild.
loadRuntimeConfig().finally(() => {
  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <QueryClientProvider client={queryClient}>
        <ToastProvider>
          <SiteScopeProvider>
            <AppRouter />
          </SiteScopeProvider>
        </ToastProvider>
      </QueryClientProvider>
    </React.StrictMode>,
  );
});
